import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, Trash2, Star } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/useAuth';
import { detectVideoPlatform, videoUrlToEmbed } from '../utils/videoEmbed';
import './AdminTrainingGallery.css';

const emptyForm = () => ({
  name: '',
  muscle_group: '',
  youtube_url: '',
});

const emptyDraft = () => ({
  name: '',
  muscle_group: '',
  youtube_url: '',
});

const PLATFORM_LABELS = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  dailymotion: 'Dailymotion',
  video: 'Video',
  enlace: 'Enlace',
  otro: '—',
};

function VideoPreviewModal({ item, onClose, onPrev, onNext, positionLabel }) {
  const embed = videoUrlToEmbed(item?.youtube_url);
  const platform = detectVideoPlatform(item?.youtube_url);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="training-modal-panel training-gallery-video-modal surface-readable bg-white rounded-2xl shadow-2xl w-full min-w-0 max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-stone-200 min-w-0">
          <div className="min-w-0">
            <h3 className="font-bold text-stone-900 text-sm break-words">{item?.name}</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              {item?.muscle_group || 'Sin grupo'} · {PLATFORM_LABELS[platform] || platform}
              {positionLabel ? ` · ${positionLabel}` : ''}
            </p>
          </div>
          <div className="flex gap-1 shrink-0">
            {onPrev && (
              <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={onPrev}>←</button>
            )}
            {onNext && (
              <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={onNext}>→</button>
            )}
            <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
        <div className="p-4 min-w-0">
          {embed ? (
            <div className="training-gallery-video-frame">
              <iframe
                title={item?.name}
                src={embed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          ) : (
            <p className="text-sm text-stone-600 mb-3">
              No se puede incrustar este enlace. Ábrelo en el navegador.
            </p>
          )}
          {item?.youtube_url && (
            <a
              href={item.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-lime-700 hover:underline break-all"
            >
              {item.youtube_url}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const GALLERY_LS_SEARCH = 'training.gallery.search';
const GALLERY_LS_MUSCLE = 'training.gallery.muscle';
const GALLERY_LS_RECENT = 'training.gallery.recent';
const GALLERY_LS_FAV = 'training.gallery.favorites';

function readJsonLs(key, fallback = []) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJsonLs(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

export default function AdminTrainingGallery() {
  const { user: currentUser } = useAuth();
  const isCoach = useMemo(() => {
    const roles = Array.isArray(currentUser?.roles) && currentUser.roles.length
      ? currentUser.roles
      : [currentUser?.rol].filter(Boolean);
    return roles.includes('entrenador') && !roles.includes('super_admin');
  }, [currentUser]);

  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [previewItem, setPreviewItem] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(() => {
    try { return sessionStorage.getItem(GALLERY_LS_SEARCH) || ''; } catch { return ''; }
  });
  const [muscleFilter, setMuscleFilter] = useState(() => {
    try { return sessionStorage.getItem(GALLERY_LS_MUSCLE) || ''; } catch { return ''; }
  });
  const [recentIds, setRecentIds] = useState(() => readJsonLs(GALLERY_LS_RECENT, []));
  const [favoriteIds, setFavoriteIds] = useState(() => readJsonLs(GALLERY_LS_FAV, []));
  const [copyingGallery, setCopyingGallery] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [importingGallery, setImportingGallery] = useState(false);
  const [galleryMeta, setGalleryMeta] = useState(null);

  useEffect(() => {
    try { sessionStorage.setItem(GALLERY_LS_SEARCH, search); } catch { /* ignore */ }
  }, [search]);

  useEffect(() => {
    try { sessionStorage.setItem(GALLERY_LS_MUSCLE, muscleFilter); } catch { /* ignore */ }
  }, [muscleFilter]);

  const fetchGallery = useCallback(async () => {
    try {
      const res = await api.get('/training/admin/gallery');
      if (res.data?.success) {
        setExercises(res.data.data || []);
        setGalleryMeta(res.data.meta || null);
      }
    } catch {
      setError('Error cargando la galería');
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const startEdit = (ex) => {
    setEditingId(ex.id);
    setDraft({
      name: ex.name || '',
      muscle_group: ex.muscle_group || '',
      youtube_url: ex.youtube_url || '',
    });
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.youtube_url?.trim()) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/training/admin/gallery', {
        ...form,
        name: form.name.trim(),
        youtube_url: form.youtube_url.trim(),
      });
      await fetchGallery();
      setForm(emptyForm());
    } catch (err) {
      setError(err?.response?.data?.error || 'Error agregando video');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRow = async (id) => {
    if (!draft.name?.trim() || !draft.youtube_url?.trim()) {
      setError('Nombre y enlace de video son obligatorios');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.put(`/training/admin/gallery/${id}`, {
        name: draft.name.trim(),
        muscle_group: draft.muscle_group.trim(),
        youtube_url: draft.youtube_url.trim(),
      });
      await fetchGallery();
      cancelEdit();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error guardando cambios');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyFromNico = async () => {
    if (exercises.length > 0) {
      const ok = window.confirm(
        'Tu galería ya tiene ejercicios. ¿Fusionar con la plantilla de Nicolás del Río? (No se borrarán los existentes)',
      );
      if (!ok) return;
    }
    setCopyingGallery(true);
    setError('');
    setCopyMessage('');
    try {
      const res = await api.post('/training/admin/gallery/copy-from-nico', {
        force: exercises.length > 0,
      });
      const report = res.data?.report;
      setCopyMessage(
        `Galería copiada: ${report?.created ?? 0} nuevos, ${report?.skipped ?? 0} sin cambios (${report?.found ?? 0} en plantilla).`,
      );
      await fetchGallery();
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo copiar la galería de Nico');
    } finally {
      setCopyingGallery(false);
    }
  };

  const downloadImportTemplate = async () => {
    try {
      const res = await api.get('/training/admin/gallery/import-template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_galeria_training.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('No se pudo descargar la plantilla Excel');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportingGallery(true);
    setError('');
    setCopyMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/training/admin/gallery/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const report = res.data?.data;
      const stats = report?.stats || {};
      setCopyMessage(
        `Importación: ${report?.created ?? 0} nuevos, ${report?.updated ?? 0} actualizados, ${report?.skipped ?? 0} sin cambios`
        + (stats.raw_rows != null
          ? ` · ${stats.raw_rows} filas Excel → ${stats.unique ?? stats.with_url ?? '—'} ejercicios únicos`
          : ''),
      );
      await fetchGallery();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error importando Excel');
    } finally {
      setImportingGallery(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este video de la galería?')) return;
    try {
      await api.delete(`/training/admin/gallery/${id}`);
      if (editingId === id) cancelEdit();
      await fetchGallery();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error eliminando video');
    }
  };

  const muscleOptions = useMemo(() => {
    const set = new Set(exercises.map((e) => e.muscle_group).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'es'));
  }, [exercises]);

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...exercises]
      .filter((ex) => {
        if (muscleFilter && ex.muscle_group !== muscleFilter) return false;
        if (!q) return true;
        return String(ex.name).toLowerCase().includes(q)
          || String(ex.muscle_group || '').toLowerCase().includes(q);
      })
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
  }, [exercises, search, muscleFilter]);

  const previewIndex = previewItem ? sorted.findIndex((ex) => ex.id === previewItem.id) : -1;

  const stepPreview = (delta) => {
    if (previewIndex < 0 || !sorted.length) return;
    const next = sorted[(previewIndex + delta + sorted.length) % sorted.length];
    openPreview(next);
  };

  const openPreview = (item) => {
    if (!item?.id) return;
    setPreviewItem(item);
    setRecentIds((prev) => {
      const next = [item.id, ...prev.filter((id) => id !== item.id)].slice(0, 12);
      writeJsonLs(GALLERY_LS_RECENT, next);
      return next;
    });
  };

  const toggleFavorite = (id) => {
    setFavoriteIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev];
      writeJsonLs(GALLERY_LS_FAV, next);
      return next;
    });
  };

  const recentItems = useMemo(
    () => recentIds.map((id) => exercises.find((e) => e.id === id)).filter(Boolean),
    [recentIds, exercises],
  );
  const favoriteItems = useMemo(
    () => favoriteIds.map((id) => exercises.find((e) => e.id === id)).filter(Boolean),
    [favoriteIds, exercises],
  );

  return (
    <div className="card training-coach-view training-light-island surface-readable d28d-admin-surface ui-light-panel bg-white">
      <h2 className="text-2xl font-bold text-stone-900 mb-2">Biblioteca de entrenamiento</h2>
      <p className="text-stone-600 mb-4 text-sm">
        {isCoach
          ? galleryMeta?.is_nicolas_anchor
            ? 'Tu biblioteca es la plantilla maestra de Nicolás del Río. Los demás coaches la heredan al registrarse; tú la editas directamente aquí.'
            : 'Por defecto tus clientes ven la biblioteca de Nicolás del Río hasta que agregues o copies la tuya. Solo tú y tus asesorados ven tu galería personalizada.'
          : 'Explora ejercicios por categoría. Continúa viendo desde recientes o favoritos.'}
      </p>

      {(recentItems.length > 0 || favoriteItems.length > 0) && (
        <div className="space-y-4 mb-6">
          {recentItems.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-stone-800 mb-2">Continuar viendo</h3>
              <div className="ef-gallery-rail">
                {recentItems.map((item) => (
                  <button key={`r-${item.id}`} type="button" className="ef-gallery-rail__item training-gallery-rail__item" onClick={() => openPreview(item)}>
                    <p className="ef-gallery-rail__name">{item.name}</p>
                    <p className="ef-gallery-rail__meta">{item.muscle_group || 'General'}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          {favoriteItems.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-stone-800 mb-2">Favoritos</h3>
              <div className="ef-gallery-rail">
                {favoriteItems.map((item) => (
                  <button key={`f-${item.id}`} type="button" className="ef-gallery-rail__item training-gallery-rail__item" onClick={() => openPreview(item)}>
                    <p className="ef-gallery-rail__name">{item.name}</p>
                    <p className="ef-gallery-rail__meta">{item.muscle_group || 'General'}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {isCoach && exercises.length < 5 && (
        <div className="bg-lime-50 border border-lime-300 rounded-xl p-4 mb-4 text-sm text-lime-900 space-y-3">
          <p>
            <strong>Onboarding:</strong> agrega al menos 5 ejercicios con video antes de crear rutinas y asignar planes.
          </p>
          {exercises.length === 0 && galleryMeta?.is_nicolas_anchor ? (
            <p className="text-stone-700">
              Eres la plantilla semilla. Si la biblioteca está vacía, importa el catálogo oficial
              {' '}(<code className="text-xs">npm run import:gallery-nico</code> en el servidor).
            </p>
          ) : null}
          {exercises.length === 0 && !galleryMeta?.is_nicolas_anchor ? (
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={copyingGallery}
              onClick={handleCopyFromNico}
            >
              {copyingGallery ? 'Copiando…' : 'Copiar galería de Nicolás del Río'}
            </button>
          ) : null}
        </div>
      )}

      {copyMessage && (
        <div className="bg-green-50 text-green-800 text-sm p-3 rounded-lg mb-4">{copyMessage}</div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg mb-4">{error}</div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button type="button" className="btn-secondary text-sm" onClick={downloadImportTemplate}>
          Descargar plantilla Excel
        </button>
        <label className="btn-secondary text-sm cursor-pointer">
          {importingGallery ? 'Importando…' : 'Importar galería (.xlsx)'}
          <input
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            disabled={importingGallery}
            onChange={handleImportExcel}
          />
        </label>
        <span className="text-xs text-stone-600">
          Columnas: CATEGORÍA · EJERCICIO · URL técnica · NOTAS (como plantilla Nicolás)
        </span>
      </div>

      <form
        onSubmit={handleAdd}
        className="bg-stone-50 border border-stone-200 p-4 rounded-xl grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6"
      >
        <label className="block">
          <span className="text-xs font-bold text-stone-700">Nombre del ejercicio</span>
          <input
            className="input mt-1 w-full"
            type="text"
            required
            placeholder="Sentadilla libre"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-xs font-bold text-stone-700">Grupo muscular</span>
          <input
            className="input mt-1 w-full"
            type="text"
            placeholder="Piernas"
            value={form.muscle_group}
            onChange={(e) => setForm({ ...form, muscle_group: e.target.value })}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs font-bold text-stone-700">Enlace de video (YouTube, Vimeo, etc.)</span>
          <input
            className="input mt-1 w-full"
            type="url"
            required
            placeholder="https://www.youtube.com/watch?v=…"
            value={form.youtube_url}
            onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
          />
        </label>
        <div className="flex flex-wrap items-center gap-4 md:col-span-2 lg:col-span-4">
          <button type="submit" className="btn-primary" disabled={loading}>
            + Agregar a la galería
          </button>
          {isCoach && exercises.length > 0 && !galleryMeta?.is_nicolas_anchor && (
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={copyingGallery}
              onClick={handleCopyFromNico}
            >
              {copyingGallery ? 'Copiando…' : 'Copiar más de la galería de Nicolás del Río'}
            </button>
          )}
        </div>
      </form>

      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="search"
          className="input flex-1 min-w-[200px]"
          placeholder="Buscar por nombre o grupo…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="input min-w-[180px]"
          value={muscleFilter}
          onChange={(e) => setMuscleFilter(e.target.value)}
        >
          <option value="">Todos los grupos</option>
          {muscleOptions.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
        <span className="text-sm text-stone-500 self-center">
          {sorted.length} / {exercises.length} ejercicios
        </span>
      </div>

      <div className="atg-table-wrap">
        <table className="atg-table">
          <thead>
            <tr>
              <th>Ejercicio</th>
              <th>Músculo</th>
              <th>Enlace video</th>
              <th>Plataforma</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((ex) => {
              const isEditing = editingId === ex.id;
              const platform = detectVideoPlatform(ex.youtube_url);
              return (
                <tr key={ex.id}>
                  <td>
                    {isEditing ? (
                      <input
                        className="input w-full text-sm"
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      />
                    ) : (
                      <span className="atg-table__name">{ex.name}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input w-full text-sm"
                        value={draft.muscle_group}
                        onChange={(e) => setDraft({ ...draft, muscle_group: e.target.value })}
                      />
                    ) : (
                      <span className="atg-table__muted">{ex.muscle_group || '—'}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input w-full text-sm"
                        type="url"
                        value={draft.youtube_url}
                        onChange={(e) => setDraft({ ...draft, youtube_url: e.target.value })}
                        placeholder="https://…"
                      />
                    ) : (
                      <span className="atg-table__link" title={ex.youtube_url || ''}>
                        {ex.youtube_url ? ex.youtube_url.replace(/^https?:\/\//, '') : '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="atg-badge">
                      {PLATFORM_LABELS[platform] || platform}
                    </span>
                  </td>
                  <td>
                    <div className="atg-actions">
                      {ex.youtube_url && !isEditing && (
                        <button
                          type="button"
                          className="atg-btn atg-btn--preview"
                          onClick={() => openPreview(ex)}
                        >
                          <Eye className="w-3.5 h-3.5" aria-hidden />
                          Vista previa
                        </button>
                      )}
                      {!isEditing && (
                        <button
                          type="button"
                          className={`atg-btn atg-btn--fav${favoriteIds.includes(ex.id) ? ' is-on' : ''}`}
                          onClick={() => toggleFavorite(ex.id)}
                          title="Favorito"
                          aria-label="Favorito"
                        >
                          <Star className="w-3.5 h-3.5" aria-hidden />
                        </button>
                      )}
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            className="atg-btn atg-btn--edit"
                            disabled={loading}
                            onClick={() => handleSaveRow(ex.id)}
                          >
                            Guardar
                          </button>
                          <button
                            type="button"
                            className="atg-btn atg-btn--ghost"
                            onClick={cancelEdit}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="atg-btn atg-btn--edit"
                          onClick={() => startEdit(ex)}
                        >
                          <Pencil className="w-3.5 h-3.5" aria-hidden />
                          Editar
                        </button>
                      )}
                      {!isEditing && (
                        <button
                          type="button"
                          className="atg-btn atg-btn--danger"
                          onClick={() => handleDelete(ex.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" aria-hidden />
                          Borrar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={5} className="atg-table__empty">
                  No hay videos en la galería. Agrega el primero con el formulario de arriba.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {previewItem && (
        <VideoPreviewModal
          item={previewItem}
          onClose={() => setPreviewItem(null)}
          onPrev={sorted.length > 1 ? () => stepPreview(-1) : null}
          onNext={sorted.length > 1 ? () => stepPreview(1) : null}
          positionLabel={previewIndex >= 0 ? `${previewIndex + 1} / ${sorted.length}` : ''}
        />
      )}
    </div>
  );
}
